/**
 * MY — Update Business Address (Section 47(2) / PD 2/2017, M32018) chatflow.
 *
 * 문서-뷰 Preset Studio:
 *   input-title → select-entity(skip 가능) → confirm
 *   + Address 입력 (business address)
 *   + constitution clause (이사 해임 DCR과 동일)
 */
import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association|schedule/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

function detectGoverningBasis(opt: string): 'constitution' | 'articles' | 'third_schedule' | null {
  const s = String(opt || '');
  if (/third\s*schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return null;
}

export const updateBusinessAddressFlow: ChatFlow = {
  id: 'update-business-address-472',
  taskCodeMatch: ['M32018'],
  categoryMatch: [
    'update business address',
    'add business address',
    'additional business address',
    'business address',
    'section 47(2)',
    's.47(2)',
  ],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Update Business Address (Section 47(2))**.\n\n` +
          `On the right, fill the live documents` +
          (docCount ? ` (${docCount} template${docCount > 1 ? 's' : ''} linked)` : '') +
          `:\n` +
          `1. Directors' Circular Resolution (Business Address)\n` +
          `2. Form PD2 (Business Address)\n` +
          `3. Section 47(2) — Notification (Records Address)\n\n` +
          `Toggle sole/multiple director, resolution basis, nature-of-business, and the records to keep. ` +
          `Use **✨** for Clause / Article No., **📅** for dates and **📍** for the address, then add **signers**.\n\n` +
          `When ready, click **Generate** to create the task and documents.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** whose business address will be added/updated. You can skip if not linked yet.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        allowSkip: true,
        analysis: {
          success: true, placeholder: '_entity', sourceResolver: 'ENTITY', officerRole: null,
          varKey: '_entity', varLabel: 'Entity', confidence: 1,
          message: 'Select Entity (Update Business Address).',
          relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Fill the documents on the right. Use **✨ / 📅 / 📍** and **Add signers**, then **Generate**.',
      widget: null,
      nextStep: null,
    },
    {
      id: 'select-clause',
      aiMessage:
        'Select a **Clause / Article No.** (same as Director Resignation), or type the number yourself.',
      widget: 'clause-number',
      widgetConfig: {
        targetVarKey: 'constitution_clause_number',
        targetEntityIndex: 0,
        clauseOptions: [],
        clauseDefaultBasis: 'constitution',
        analysis: {
          success: true,
          placeholder: 'constitution_clause_number',
          sourceResolver: 'SELECT',
          officerRole: null,
          varKey: 'constitution_clause_number',
          varLabel: 'Clause / Article No.',
          confidence: 1,
          message: 'Select or enter Clause / Article No.',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    { id: 'officer-role', aiMessage: 'Please select a role for the person you want to look up.', widget: 'officer-role-buttons', awaitEvent: true },
    { id: 'officer-list', aiMessage: 'Select a person from the list.', widget: 'officer-list', awaitEvent: true },
    { id: 'officer-detail', aiMessage: 'Person details loaded. The document has been pre-filled.', widget: 'officer-field-picker', awaitEvent: true },
    { id: 'address-input', aiMessage: 'Enter the address below.', widget: 'address-input', awaitEvent: true },
    { id: 'address-list', aiMessage: 'This person has multiple addresses. Pick the one to use.', widget: 'address-options', awaitEvent: true },
    {
      id: 'pick-msic',
      aiMessage: 'Search and select an **MSIC / Nature of Business**.',
      widget: 'business-code',
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd,
          entityName,
          companyName: entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || ent.regNo || '',
          companyType: ent.entityType || ent.companyType || '',
          entityType: ent.entityType || ent.companyType || '',
          entitySkipped: false,
          entityReselect: false,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'entity-skip' && context.currentStepId === 'select-entity') {
      return {
        messages: [{ role: 'user', content: 'Skipped entity — continue without a linked company.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { entityCd: '', entityName: '', icpCd: '', registrationNo: '', tlinkData: null, entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      if (ctx.fieldGroup === 'constitution-clause' || ctx.key === 'constitution_clause_number') {
        const options = Array.isArray(ctx.clauseOptions)
          ? ctx.clauseOptions.map((o: any) => String(o ?? '').trim()).filter(Boolean)
          : [];
        return {
          messages: [{
            role: 'user',
            content: 'Looking up **Clause / Article No.**…',
            timestamp: Date.now(),
            stepId: 1,
          }],
          nextStepId: 'select-clause',
          nextStepWidgetData: {
            targetVarKey: 'constitution_clause_number',
            clauseOptions: options,
            clauseDefaultBasis: ctx.defaultBasis || 'constitution',
            defaultValue: ctx.defaultValue || '',
            analysis: {
              success: true,
              placeholder: 'constitution_clause_number',
              sourceResolver: 'SELECT',
              officerRole: null,
              varKey: 'constitution_clause_number',
              varLabel: 'Clause / Article No.',
              confidence: 1,
              message: 'Select or enter Clause / Article No.',
              relatedFields: [],
              options: [],
            } as PlaceholderAnalysis,
          },
        };
      }
      if (ctx.lookupType === 'msic' || ctx.key === 'nature_of_business') {
        return {
          messages: [{
            role: 'user',
            content: 'Look up **Nature of Business** (MSIC)…',
            timestamp: Date.now(),
            stepId: 1,
          }],
          formUpdates: { _msicTargetKey: 'nature_of_business' },
          nextStepId: 'pick-msic',
          nextStepWidgetData: { targetVarKey: 'nature_of_business' },
        };
      }
      if (ctx.key === 'company_name' || ctx.lookupType === 'entity') {
        return {
          messages: [{
            role: 'user',
            content: 'Search and select a **company**…',
            timestamp: Date.now(),
            stepId: 1,
          }],
          nextStepId: 'select-entity',
        };
      }
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!ctx.defaultRole) {
        return {
          messages: [{ role: 'user', content: `Look up a person for **${ctx.fieldGroup || 'this field'}**.`, timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'officer-role',
          nextStepWidgetData: { entityCd },
        };
      }
      return {
        messages: [{ role: 'user', content: `Looking up **${ctx.defaultRole}**...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
      };
    }

    if (eventName === 'clause-number-select') {
      const pick = payload || {};
      const clauseNo = String(pick.clauseNumber || '').trim();
      const rawOption = String(pick.rawOption || '').trim();
      const basis =
        pick.governingBasis ||
        detectGoverningBasis(rawOption) ||
        (clauseNo ? 'constitution' : null);
      const label =
        basis === 'third_schedule'
          ? 'Third Schedule'
          : clauseNo
            ? `${basis === 'articles' ? 'Article' : 'Clause'} ${clauseNo}`
            : 'Clause';
      const formUpdates: Record<string, any> = {
        constitution_clause_number: basis === 'third_schedule' ? '' : clauseNo,
        _clauseSyncAt: Date.now(),
        _clauseRawOption: rawOption,
      };
      if (basis) formUpdates._resignGoverningBasis = basis;
      return {
        messages: [{
          role: 'user',
          content: `Governing document: **${label}**`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        formUpdates,
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'value-submit' && context.currentStepId === 'select-clause') {
      const raw = typeof payload === 'string' ? payload : payload?.value || payload;
      const rawStr = String(raw || '').trim();
      if (!rawStr) return {};
      const clauseNo = parseClauseNo(rawStr);
      const basis = detectGoverningBasis(rawStr);
      const formUpdates: Record<string, any> = {
        constitution_clause_number: clauseNo || rawStr,
        _clauseSyncAt: Date.now(),
        _clauseRawOption: rawStr,
      };
      if (basis) formUpdates._resignGoverningBasis = basis;
      return {
        messages: [{
          role: 'user',
          content: `Clause / Article No.: **${clauseNo || rawStr}**`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        formUpdates,
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      return {
        messages: [{ role: 'user', content: 'Person selected.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
        nextStepWidgetData: payload,
      };
    }

    // MSIC 선택 확정
    if (eventName === 'value-submit' && context.currentStepId === 'pick-msic') {
      let code = '';
      let name = '';
      if (payload && typeof payload === 'object') {
        code = String(payload.code || '').trim();
        name = String(payload.name || payload.value || '').trim();
      } else {
        name = String(payload || '').trim();
      }
      if (!name && !code) return {};
      const display = code && name ? `${code} - ${name}` : (name || code);
      return {
        messages: [{
          role: 'user',
          content: `Nature of Business: **${display}**`,
          timestamp: Date.now(),
          stepId: 1,
        }],
        formUpdates: {
          nature_of_business: display,
          _msicTargetKey: '',
        },
        nextStepId: 'confirm',
      };
    }

    // 문서 📍 → 챗봇 주소 입력 폼
    if (eventName === 'request-address-input') {
      return {
        messages: [
          { role: 'user', content: 'Enter an address.', timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [
          {
            role: 'user',
            content: `Choose an address for **${subject}**.`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          {
            role: 'user',
            content: `Selected: **${opt.label || opt.type}**`,
            timestamp: Date.now(),
            stepId: 1,
          },
        ],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
